import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { SimpleSelect } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { DashboardView } from 'apps/sigma2-ui/src/components/overview/DashboardView';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { DynamicPipelineAnalysisTab } from 'apps/sigma2-ui/src/components/overview/DynamicPipelineAnalysisTab';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useSelectedOrganizationId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedOrganizationId';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { useSelectedDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseRevisionId';
import { useSelectedDatabaseId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseId';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { isEntitledToPipelineAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { tryTranslateDynamic } from 'libs/end-to-end/src/phrase-utils';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { getDatabaseRevisionsForUser } from 'libs/end-to-end/src/get-database-revisions-for-user';

interface OverviewPageFilterValueProps {
  tabValue: string;
  displayedValue: string;
  automationTag?: string;
}

export const OverviewPage = () => {
  const { analysisName: analysisNameRaw } = useParams<{
    analysisName: string;
  }>();

  const language = useLanguage();
  const { myUser, userAuthenticatedApiCall, loadUser } = useUser();
  const { myObservation, updateTreatments } = useObservation();

  const analysisName = useMemo(() => {
    return isEntitledToPipelineAnalysis({
      features: myObservation?.features ?? [],
    })
      ? analysisNameRaw
      : undefined;
  }, [analysisNameRaw, myObservation?.features]);

  const selectedDatabaseRevision = useSelectedDatabaseRevisionId();
  const selectedDatabaseId = useSelectedDatabaseId();
  const selectedOrganization = useSelectedOrganizationId();

  const dbRevisionDropdownOptions = useMemo(
    () =>
      getDatabaseRevisionsForUser({
        myUser,
        databaseId: selectedDatabaseId,
      }),
    [myUser, selectedDatabaseId],
  );

  const tabs = useMemo<OverviewPageFilterValueProps[]>(() => {
    return [
      {
        tabValue: 'dashboard',
        displayedValue: language.overviewNavigationDashboard(),
        automationTag: 'dashboard',
      },
      ...(myObservation?.pipelineAnalysisConfiguration &&
      isEntitledToPipelineAnalysis({ features: myObservation.features })
        ? myObservation.pipelineAnalysisConfiguration.map((conf) => ({
            tabValue: conf.name,
            displayedValue: getTranslatedContentAsString(
              tryTranslateDynamic({
                phrase: conf.name,
                customValue: '',
                logger: uiAppLogger,
                language,
              }),
            ),
            automationTag: conf.name,
          }))
        : []),
    ].filter(Boolean) as OverviewPageFilterValueProps[];
  }, [language, myObservation]);

  const selectedTab = useMemo<OverviewPageFilterValueProps>(() => {
    return tabs.find((value) => value.tabValue === analysisName) ?? tabs[0];
  }, [tabs, analysisName]);

  const analytics = useAnalytics();

  useEffect(() => {
    if (!analysisName) {
      analytics.trackEvent({
        eventName: 'Overview Page Viewed',
        eventProperties: {
          selectedTab: 'Dashboard',
        },
      });
    } else {
      analytics.trackEvent({
        eventName: 'Overview Page Viewed',
        eventProperties: {
          selectedTab: analysisName,
        },
      });
    }
  }, [analysisName, analytics]);

  const selectObservationType = useCallback(
    (type: ObservationLocationType) => {
      loadUser({
        observation: type,
        organizationId: selectedOrganization,
        onLoaded: (observation) => {
          if (!observation) {
            return;
          }
        },
      });
    },
    [loadUser, selectedOrganization],
  );

  const pageReference = useRef(null);

  useEffect(() => {
    const pgReference = pageReference.current as HTMLElement | null;
    pgReference?.focus();
  }, []);

  useAuthenticatedPageRedirect();
  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const navigate = useNavigateSafe();

  return (
    <MainLayout showBackToTop>
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <Helmet>
        <title>{documentTitle(language.overview())}</title>
      </Helmet>
      <PageSection>
        <Spacing direction="vertical" size="md">
          <div className="mt-0">
            <S2Typography
              variant="headline-3"
              translatedContent={language.overview()}
            />

            <Spacing direction="horizontal" size="none">
              <TabsRadioGroup
                currValue={selectedTab?.tabValue}
                values={tabs}
                onChange={(value: string) => {
                  if (value === 'dashboard') {
                    navigate('/overview');
                  } else {
                    navigate(`/overview/${value}`);
                  }
                }}
                minWidth="auto"
                gap="md"
                underlineBgClass="bg-secondary"
                renderTab={(tab) => {
                  const filter = tab.value as OverviewPageFilterValueProps;
                  return (
                    <S2Typography
                      variant="headline-6-alt"
                      translatedContent={language.dangerousUserInput({
                        value: filter.displayedValue,
                      })}
                      dangerouslyOverrideClassNameFn={
                        filter.tabValue === selectedTab?.tabValue
                          ? replaceTextColor('text-secondary')
                          : undefined
                      }
                    />
                  );
                }}
              />

              <Spacing
                direction="vertical"
                size="none"
                className="min-h-12 w-52 border-b-1 border-gray-400 justify-center"
              >
                {dbRevisionDropdownOptions.length !== 0 && (
                  <SimpleSelect
                    selectedOptionValue={selectedDatabaseRevision}
                    updateOptions={(newSelection) => {
                      const foundModel = dbRevisionDropdownOptions.find(
                        (option) => option.value === newSelection,
                      );
                      if (!foundModel) {
                        return;
                      }

                      selectObservationType({
                        type: 'db-revision',
                        databaseId: foundModel.model.databaseId,
                        databaseRevisionId: foundModel.model.databaseRevisionId,
                      });
                    }}
                    options={dbRevisionDropdownOptions}
                  />
                )}
              </Spacing>
            </Spacing>

            <div className="p-6" />

            <ObservationLoadingAndFailureIndicators
              isLoadingStill={isLoadingStill}
              failedToLoadObservation={failedToLoadObservation}
              offset={307}
            />

            {!isLoadingStill &&
              selectedTab?.tabValue === 'dashboard' &&
              myObservation && (
                <DashboardView
                  myUser={myUser}
                  userAuthenticatedApiCall={userAuthenticatedApiCall}
                  organizationId={myUser?.organization?.id ?? null}
                  myObservation={myObservation}
                  updateTreatments={updateTreatments}
                  language={language}
                  overviewPageConfiguration={
                    myObservation.overviewPageConfiguration
                  }
                  navigate={navigate}
                />
              )}

            {!isLoadingStill}

            {!isLoadingStill && analysisName && (
              <DynamicPipelineAnalysisTab
                organizationId={myUser?.organization?.id ?? null}
                myObservation={myObservation}
                userAuthenticatedApiCall={userAuthenticatedApiCall}
                analysisName={analysisName}
              />
            )}
          </div>
        </Spacing>
      </PageSection>
    </MainLayout>
  );
};
